/**
 * Catalog for the Skill Impact Visualization screen.
 *
 * The impact tool used to hand the GM one flat select of every skill in the
 * game — 312 options, unordered, unlabelled. A skill is only meaningful next to
 * its siblings: the school teaches it and the grade says when it is learned. So
 * this loads the three catalogs the picker navigates by (paths, schools,
 * skills) and joins them, rather than the skill list alone.
 *
 * The skill payload carries `school` as a bare UUID, which is why the school
 * has to be fetched separately and looked up here.
 */

import { SchoolGameApi } from '@/api/backendService.js';
import { schoolAccent } from '@/services/GameMasterCompendiumService.js';

/** Schools the fixtures attach to no path — GM tools, weapons, artefacts. */
const UNALIGNED = {
    id: 'unaligned',
    name: 'Unaligned',
    icon: null,
    description: 'Schools that belong to no path — game master tools, weapons and artefacts.',
};

function unwrap(response) {
    const payload = response?.data;
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.results)) return payload.results;
    return [];
}

function toSkill(raw) {
    return {
        id: raw.id,
        name: raw.name,
        description: raw.description || '',
        grade: raw.grade ?? 9,
        type: raw.type || null,
        icon: raw.icon || null,
        schoolId: raw.school ? String(raw.school) : null,
        impact: Array.isArray(raw.impact) ? raw.impact : [],
        cost: Array.isArray(raw.cost) ? raw.cost : [],
        min_range: raw.min_range,
        max_range: raw.max_range,
    };
}

class GameMasterSkillImpactService {
    constructor() {
        this.cache = null;
        this.inFlight = null;
    }

    async loadCatalog({ force = false } = {}) {
        if (force) {
            this.cache = null;
            this.inFlight = null;
        }
        if (this.cache) return this.cache;
        if (!this.inFlight) {
            this.inFlight = this.fetchAll()
                .then((catalog) => {
                    this.cache = catalog;
                    return catalog;
                })
                .finally(() => {
                    this.inFlight = null;
                });
        }
        return this.inFlight;
    }

    async fetchAll() {
        const [paths, schools, skills] = await Promise.all([
            SchoolGameApi.schoolPathsGetAllPathsRetrieve().then(unwrap),
            SchoolGameApi.schoolSchoolsGetAllSchoolsRetrieve().then(unwrap),
            SchoolGameApi.schoolSchoolsGetAllSkillsRetrieve().then(unwrap),
        ]);
        return this.build({ paths, schools, skills });
    }

    build({ paths, schools, skills }) {
        const parsed = skills.map(toSkill);

        const bySchool = new Map();
        for (const skill of parsed) {
            if (!skill.schoolId) continue;
            const bucket = bySchool.get(skill.schoolId);
            if (bucket) bucket.push(skill);
            else bySchool.set(skill.schoolId, [skill]);
        }

        // A school with no skills has nothing to analyse, and 23 tiles is
        // already a wide rail — leaving the empty ones out keeps the rail
        // scannable rather than complete.
        const decorated = schools
            .map((school) => ({
                id: String(school.id),
                name: school.name,
                icon: school.icon || null,
                description: school.description || '',
                accent: schoolAccent(school.name),
                isBase: Boolean(school.is_base),
                pathIds: (school.path || []).map(String),
                skills: bySchool.get(String(school.id)) || [],
            }))
            .filter((school) => school.skills.length > 0)
            .sort((a, b) => a.name.localeCompare(b.name));

        const groups = paths.map((path) => ({
            id: String(path.id),
            name: path.name,
            icon: path.icon || null,
            description: path.description || '',
            schools: decorated.filter((school) => school.pathIds.includes(String(path.id))),
        }));

        const orphans = decorated.filter((school) => school.pathIds.length === 0);
        if (orphans.length) groups.push({ ...UNALIGNED, schools: orphans });

        return {
            groups: groups.filter((group) => group.schools.length > 0),
            schools: decorated,
            schoolById: new Map(decorated.map((school) => [school.id, school])),
            skillCount: parsed.length,
        };
    }
}

export default new GameMasterSkillImpactService();
